import {
  IChartData,
  type TCommonParamsResponse,
  ICommonParamsResponse,
  IMeasuresResponse,
  IPointResponse,
} from "../interfaces";
import { getLineColor } from "../utils/line-colors";
import { ITableData, ITableItem, PARAMETER_TYPE } from "../components/table/interfaces";

interface IMeasuresTransformedData {
  chart: IChartData[];
  table: ITableData;
}

const createChartDataBase = (
  name: string,
  color: string,
  units: string,
  extras?: {
    parameterName?: string;
    idMeasure?: number;
    idPoint?: number;
    pointDirection?: number;
    unitCode?: number | null;
    alarmLevel2?: number | null;
    alarmLevel3?: number | null;
    alarmLevel4?: number | null;
    isAmplitudePhase?: boolean;
  },
): Omit<IChartData, "items"> => ({
  name,
  color,
  units,
  ...extras,
});

const normalizeCommonParams = (data: TCommonParamsResponse[]): ICommonParamsResponse[] =>
  data.map(item => ({
    idTrain: "idTrain" in item ? item.idTrain : item.idtrain,
    Date: "Date" in item ? item.Date : item.date,
    paramID: "paramID" in item ? item.paramID : item.paramid,
    Value: "Value" in item ? item.Value : item.value,
  }));

export const transformCommonParamsToChartData = (
  json: TCommonParamsResponse[],
  measureName: string,
  type: PARAMETER_TYPE,
): IMeasuresTransformedData => {
  if (!json.length) {
    return {
      chart: [],
      table: { type, rows: [], items: [] } as ITableData,
    };
  }

  const normalizedJson = normalizeCommonParams(json);

  const dateSet = new Set<string>();
  normalizedJson.forEach(({ Date }) => dateSet.add(Date));

  const rows = Array.from(dateSet).sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());
  const rowIndexMap = new Map<string, number>();
  rows.forEach((d, i) => rowIndexMap.set(d, i));

  const chart: IChartData[] = [];
  const tableItems: ITableItem[] = [];

  const tableItemByParam = new Map<number, ITableItem>();
  const chartByParam = new Map<number, IChartData>();

  normalizedJson.forEach((param, i) => {
    const paramID = param.paramID;

    const tableItem: ITableItem = {
      name: measureName,
      values: new Array(rows.length).fill(null),
    };

    tableItems.push(tableItem);
    tableItemByParam.set(paramID, tableItem);

    const chartData: IChartData = {
      name: measureName,
      color: getLineColor(i),
      units: measureName,
      idTrain: param.idTrain,
      paramId: paramID,
      items: [],
    };

    chart.push(chartData);
    chartByParam.set(paramID, chartData);
  });

  normalizedJson.forEach(param => {
    const { Date, paramID, Value } = param;
    const rowIdx = rowIndexMap.get(Date);
    if (rowIdx === undefined) return;

    const tableItem = tableItemByParam.get(paramID);
    if (tableItem) {
      tableItem.values[rowIdx] = Value;
    }

    const chartData = chartByParam.get(paramID);
    if (!chartData) return;

    chartData.items.push({ date: Date, value: Value });
  });

  chart.forEach(cd => {
    cd.items.sort((a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf());
  });

  const table: ITableData = {
    type,
    rows,
    items: tableItems.filter(item => item.values.some(v => v !== null)),
  };

  return {
    chart: chart.filter(cd => cd.items.length > 0),
    table,
  };
};

export const transformMeasuresDataMultiplePoints = (
  json: IMeasuresResponse[],
  points: IPointResponse[],
  measureName: string,
  type: PARAMETER_TYPE,
  useValue2: boolean = false,
): IMeasuresTransformedData => {
  if (!json.length || !points.length) {
    return {
      chart: [],
      table: { type, rows: [], items: [] } as ITableData,
    };
  }

  const dateSet = new Set<string>();
  json.forEach(m => dateSet.add(m.Date));

  const rows = Array.from(dateSet).sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());

  const rowIndexMap = new Map<string, number>();
  rows.forEach((d, i) => rowIndexMap.set(d, i));

  const chart: IChartData[] = [];
  const tableItems: ITableItem[] = [];

  const tableItemByPoint = new Map<number, ITableItem>();
  const chartByPoint = new Map<number, IChartData[]>();

  const unitsValue1 = String(json[0]?.Units);
  const unitsValue2 = String(133);
  const unitCodeValue1 = json[0]?.Units ?? null;
  const unitCodeValue2 = 133;

  points.forEach((point, i) => {
    const idPoint = +point.id;
    const idMeasure = json.find(measure => measure.idPoint === idPoint)?.idMeasure;
    const pointName = point.title;
    const pointDirection = point.Direction ?? null;

    const tableItem: ITableItem = {
      idPoint,
      name: pointName,
      pointDirection: String(pointDirection),
      unitCode: unitCodeValue1,
      unitCode2: useValue2 ? unitCodeValue2 : null,
      values: new Array(rows.length).fill(null),
      values2: new Array(rows.length).fill(null),
    };

    tableItems.push(tableItem);
    tableItemByPoint.set(idPoint, tableItem);

    const chartsForPoint: IChartData[] = [];

    if (useValue2) {
      const amplitude: IChartData = {
        ...createChartDataBase(pointName, getLineColor(i * 2), unitsValue1, {
          parameterName: measureName,
          idMeasure,
          idPoint,
          pointDirection,
          unitCode: unitCodeValue1,
          isAmplitudePhase: true,
        }),
        items: [],
      };

      const phase: IChartData = {
        ...createChartDataBase(pointName, getLineColor(i * 2 + 1), unitsValue2, {
          parameterName: measureName,
          idMeasure,
          idPoint,
          pointDirection,
          unitCode: unitCodeValue2,
          isAmplitudePhase: true,
        }),
        items: [],
      };

      chartsForPoint.push(amplitude, phase);
      chart.push(amplitude, phase);
    } else {
      const single: IChartData = {
        ...createChartDataBase(pointName, getLineColor(i), unitsValue1, {
          parameterName: measureName,
          idMeasure,
          idPoint,
          pointDirection,
          unitCode: unitCodeValue1,
        }),
        items: [],
      };

      chartsForPoint.push(single);
      chart.push(single);
    }

    chartByPoint.set(idPoint, chartsForPoint);
  });

  json.forEach(measure => {
    if (measure.Date == null || typeof measure.Date !== "string") {
      console.warn("trendMappers invalid Date field", measure);
    }
    const { Date, idPoint, Value1, Value2, AlarmLevel2, AlarmLevel3, AlarmLevel4, RPM } = measure;

    const rowIdx = rowIndexMap.get(Date);
    if (rowIdx === undefined) return;

    const tableItem = tableItemByPoint.get(idPoint);
    if (tableItem) {
      tableItem.values[rowIdx] = Value1;
      if (useValue2) {
        tableItem.values2![rowIdx] = Value2 ?? null;
      }
    }

    const charts = chartByPoint.get(idPoint);
    if (!charts) return;

    charts.forEach(cd => {
      if (cd.alarmLevel2 === undefined && AlarmLevel2 !== undefined) {
        cd.alarmLevel2 = AlarmLevel2;
        cd.alarmLevel3 = AlarmLevel3;
        cd.alarmLevel4 = AlarmLevel4;
      }
    });

    if (useValue2) {
      charts[0].items.push({ date: Date, value: Value1, rotationFrequencyRpm: RPM ?? null });

      if (Value2 !== null && Value2 !== undefined) {
        charts[1].items.push({ date: Date, value: Value2, rotationFrequencyRpm: RPM ?? null });
      }
    } else {
      charts[0].items.push({ date: Date, value: Value1, rotationFrequencyRpm: RPM ?? null });
    }
  });

  chart.forEach(cd => {
    cd.items.sort((a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf());
  });

  const table: ITableData = {
    type,
    rows,
    items: tableItems.filter(item => item.values.some(v => v !== null)),
  };

  return {
    chart: chart.filter(cd => cd.items.length > 0),
    table,
  };
};

export const transformMeasuresDataSinglePoint = (
  json: IMeasuresResponse[],
  pointName: string,
  measureName: string,
  type: PARAMETER_TYPE,
  useValue2 = false,
): IMeasuresTransformedData => {
  if (!json.length) {
    return {
      chart: [],
      table: { type, rows: [], items: [] },
    };
  }

  const rows = Array.from(new Set(json.map(m => m.Date))).sort((a, b) => new Date(a).valueOf() - new Date(b).valueOf());

  const rowIndexMap = new Map(rows.map((d, i) => [d, i]));

  const unitsValue1 = String(json[0]?.Units);
  const unitsValue2 = String(133);
  const unitCodeValue1 = json[0]?.Units ?? null;
  const unitCodeValue2 = 133;

  const tableItem: ITableItem = {
    name: pointName,
    unitCode: unitCodeValue1,
    unitCode2: useValue2 ? unitCodeValue2 : null,
    values: new Array(rows.length).fill(null),
    values2: new Array(rows.length).fill(null),
  };

  const chart: IChartData[] = [];

  const amplitude = createChartDataBase(pointName, getLineColor(0), unitsValue1, {
    parameterName: measureName,
    idMeasure: json[0].idMeasure,
    idPoint: json[0].idPoint,
    unitCode: unitCodeValue1,
    isAmplitudePhase: useValue2,
  });

  const amplitudeSeries: IChartData = { ...amplitude, items: [] };
  chart.push(amplitudeSeries);

  let phaseSeries: IChartData | null = null;

  if (useValue2) {
    phaseSeries = {
      ...createChartDataBase(pointName, getLineColor(1), unitsValue2, {
        parameterName: measureName,
        idMeasure: json[0].idMeasure,
        idPoint: json[0].idPoint,
        unitCode: unitCodeValue2,
        isAmplitudePhase: true,
      }),
      items: [],
    };
    chart.push(phaseSeries);
  }

  json.forEach(({ Date, Value1, Value2, AlarmLevel2, AlarmLevel3, AlarmLevel4, RPM }) => {
    const idx = rowIndexMap.get(Date);
    if (idx === undefined) return;

    tableItem.values[idx] = Value1;
    if (useValue2) {
      tableItem.values2![idx] = Value2 ?? null;
    }

    amplitudeSeries.items.push({ date: Date, value: Value1, rotationFrequencyRpm: RPM ?? null });

    if (useValue2 && Value2 != null && phaseSeries) {
      phaseSeries.items.push({ date: Date, value: Value2, rotationFrequencyRpm: RPM ?? null });
    }

    if (amplitudeSeries.alarmLevel2 === undefined) {
      amplitudeSeries.alarmLevel2 = AlarmLevel2;
      amplitudeSeries.alarmLevel3 = AlarmLevel3;
      amplitudeSeries.alarmLevel4 = AlarmLevel4;

      if (phaseSeries) {
        phaseSeries.alarmLevel2 = AlarmLevel2;
        phaseSeries.alarmLevel3 = AlarmLevel3;
        phaseSeries.alarmLevel4 = AlarmLevel4;
      }
    }
  });

  chart.forEach(cd => cd.items.sort((a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf()));

  return {
    chart: chart.filter(cd => cd.items.length),
    table: {
      type,
      rows,
      items: [tableItem],
    },
  };
};
